import type { ActivityFilters } from '../pages/activity/types';

// Session-scoped activity list state (filters + page), so it survives opening an activity and coming back.

const FILTERS_KEY_PREFIX = 'activity-filters-';
const PAGE_KEY_PREFIX = 'activity-page-';

function getFiltersKey(companyId: number): string {
    return `${FILTERS_KEY_PREFIX}${companyId}`;
}

function getPageKey(companyId: number): string {
    return `${PAGE_KEY_PREFIX}${companyId}`;
}

function withStorage<T>(action: string, fallback: T, operation: () => T): T {
    try {
        return operation();
    } catch (error) {
        console.error(`Failed to ${action}:`, error);
        return fallback;
    }
}

function isEntityList(value: unknown): boolean {
    return Array.isArray(value) && value.every(
        item => typeof item === 'object' && item !== null && typeof (item as { id?: unknown }).id === 'number'
    );
}

function isOptionalOfType(value: unknown, type: 'string' | 'boolean'): boolean {
    return value === undefined || typeof value === type;
}

function parseFilters(raw: string): ActivityFilters | undefined {
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== 'object') return undefined;

    const isValid = isEntityList(parsed.products)
        && isEntityList(parsed.clients)
        && isEntityList(parsed.stocks)
        && isEntityList(parsed.providers)
        && isEntityList(parsed.activityTypes)
        && isOptionalOfType(parsed.serialNumber, 'string')
        && isOptionalOfType(parsed.fromDate, 'string')
        && isOptionalOfType(parsed.toDate, 'string')
        && isOptionalOfType(parsed.showOnlyActive, 'boolean');

    return isValid ? (parsed as unknown as ActivityFilters) : undefined;
}

export function loadActivityFilters(companyId: number): ActivityFilters | undefined {
    return withStorage('load activity filters', undefined, () => {
        const raw = sessionStorage.getItem(getFiltersKey(companyId));
        return raw ? parseFilters(raw) : undefined;
    });
}

export function saveActivityFilters(companyId: number, filters: ActivityFilters): void {
    withStorage('save activity filters', undefined, () => {
        sessionStorage.setItem(getFiltersKey(companyId), JSON.stringify(filters));
    });
}

export function loadActivityPage(companyId: number): number {
    return withStorage('load activity page', 0, () => {
        const page = Number(sessionStorage.getItem(getPageKey(companyId)));
        return Number.isInteger(page) && page > 0 ? page : 0;
    });
}

export function saveActivityPage(companyId: number, page: number): void {
    withStorage('save activity page', undefined, () => {
        sessionStorage.setItem(getPageKey(companyId), String(page));
    });
}

export function clearActivityPage(companyId: number): void {
    withStorage('clear activity page', undefined, () => {
        sessionStorage.removeItem(getPageKey(companyId));
    });
}

// Logout is a full page redirect that keeps sessionStorage alive in the tab, so the next user
// would otherwise inherit the previous user's filters
export function clearAllActivityListState(): void {
    withStorage('clear activity list state', undefined, () => {
        Object.keys(sessionStorage)
            .filter(key => key.startsWith(FILTERS_KEY_PREFIX) || key.startsWith(PAGE_KEY_PREFIX))
            .forEach(key => sessionStorage.removeItem(key));
    });
}
